import React, { useState, useRef, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { downloadCsvTemplate, downloadErrorReport } from '../../utils/referralCsvTemplate';

const BulkReferralUpload = ({ healthSystemId, defaultProcedureType, onSuccess, onProcessingStatusChange, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // WebSocket and async processing states
  const [uploadId, setUploadId] = useState(null);
  const [wsConnection, setWsConnection] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);

  // Available task types toggle
  const [showAvailableTypes, setShowAvailableTypes] = useState(false);
  const [availableTypes, setAvailableTypes] = useState(null);
  const [loadingAvailableTypes, setLoadingAvailableTypes] = useState(false);

  // WebSocket connection management
  useEffect(() => {
    if (!uploadId) return;

    // Get WebSocket base URL from API base URL
    const apiBaseUrl = process.env.REACT_APP_MIRASOL_BACKEND_URL || 'http://localhost:8001';
    const wsProtocol = apiBaseUrl.startsWith('https') ? 'wss:' : 'ws:';
    const wsHost = apiBaseUrl.replace(/^https?:\/\//, '');

    // Get JWT token for WebSocket authentication
    const token = localStorage.getItem('admin_api_token');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/bulk-upload/${uploadId}/`;

    // Create WebSocket without token (too large for headers)
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[WS] WebSocket opened, sending auth token...');
      // Send token as first message after connection
      ws.send(JSON.stringify({
        type: 'authenticate',
        token: token
      }));

      setProcessingStatus({
        status: 'connecting',
        progress: 0,
        message: 'Connected! Waiting for processing to start...'
      });
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'authenticated') {
        // Authentication successful, update status
        console.log('[WS] Authentication successful, waiting for progress updates...');
        setProcessingStatus({
          status: 'authenticated',
          progress: 0,
          message: 'Authenticated! Waiting for processing to start...'
        });
      } else if (data.type === 'progress_update') {
        setProcessingStatus(data);

        // Notify parent of processing status change
        if (onProcessingStatusChange) {
          onProcessingStatusChange(data);
        }

        // If completed or failed, close WebSocket after delay
        if (data.status === 'completed' || data.status === 'failed') {
          setTimeout(() => {
            ws.close();
            setWsConnection(null);
          }, 1000);
        }
      } else if (data.type === 'error') {
        // Handle error messages from backend
        console.error('[WS] Error from backend:', data.message);
        setError(data.message || 'WebSocket error');
        ws.close();
        setWsConnection(null);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection lost. The upload may still be processing...');
    };

    ws.onclose = () => {
      // WebSocket closed
    };

    setWsConnection(ws);

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [uploadId]);

  // Handle completion
  useEffect(() => {
    // Handle all completion statuses: 'completed', 'failed', 'partial_success'
    const isComplete = processingStatus?.status === 'completed' ||
                      processingStatus?.status === 'failed' ||
                      processingStatus?.status === 'partial_success';

    if (isComplete && processingStatus.results) {
      // Preserve fallback message in upload results
      setUploadResults({
        ...processingStatus.results,
        fallback_message: processingStatus.fallback_message,
        harmonization_failed: processingStatus.harmonization_failed,
        completion_status: processingStatus.status
      });

      // Call success callback only if at least some succeeded
      if (processingStatus.results.created_count > 0 && onSuccess) {
        setTimeout(() => {
          onSuccess(processingStatus.results);
        }, 2000);
      }
    }
  }, [processingStatus, onSuccess]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Basic validation
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    // Check file size (10 MB limit)
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 10 MB.');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setUploadResults(null);
    setProcessingStatus(null);
    setUploadId(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      const event = { target: { files: [file] } };
      handleFileSelect(event);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !healthSystemId) {
      setError('Please select a file and ensure a health system is selected');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResults(null);
    setProcessingStatus({
      status: 'uploading',
      progress: 0,
      message: 'Uploading file to server...'
    });

    try {
      const formData = new FormData();
      formData.append('csv_file', selectedFile);
      formData.append('health_system_id', healthSystemId);

      if (defaultProcedureType && defaultProcedureType.trim()) {
        formData.append('default_case_name', defaultProcedureType.trim());
      }

      const response = await adminApi.post('admin/referrals/bulk-upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.upload_id) {
        setUploadId(response.data.upload_id);
        // WebSocket will connect via useEffect
      } else {
        setError('Upload failed: No upload ID returned');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResults(null);
    setError(null);
    setProcessingStatus(null);
    setUploadId(null);
    setShowAvailableTypes(false);
    setAvailableTypes(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (wsConnection) {
      wsConnection.close();
      setWsConnection(null);
    }
  };

  const handleDownloadTemplate = () => {
    downloadCsvTemplate(true);
  };

  const handleDownloadErrorReport = () => {
    if (uploadResults && uploadResults.failed && uploadResults.failed.length > 0) {
      downloadErrorReport(uploadResults.failed);
    }
  };

  const handleToggleAvailableTypes = async () => {
    if (showAvailableTypes) {
      setShowAvailableTypes(false);
      return;
    }

    // Fetch available types if not already loaded
    if (!availableTypes) {
      setLoadingAvailableTypes(true);
      try {
        const response = await adminApi.get('admin/referral-types/', {
          params: { health_system_id: healthSystemId }
        });
        setAvailableTypes(response.data.referral_types || []);
      } catch (err) {
        setError('Failed to load available task types');
      } finally {
        setLoadingAvailableTypes(false);
      }
    }
    setShowAvailableTypes(true);
  };

  return (
    <>
      <div className="bulk-referral-upload">
        {error && (
          <div className="alert alert-danger mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Health System Reminder */}
        {!healthSystemId && (
          <div className="alert alert-warning mb-3">
            <i className="fas fa-info-circle me-2"></i>
            Please select a health system above before uploading
          </div>
        )}

        {/* Download Template */}
        <div className="mb-4">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleDownloadTemplate}
          >
            <i className="fas fa-download me-2"></i>
            Download CSV Template
          </button>
          <small className="text-muted ms-3">
            Download template with example data to get started
          </small>
        </div>

        {/* File Upload Area */}
        {!uploadResults && !processingStatus && (
          <div
            className="border rounded p-4 mb-3 text-center"
            style={{
              borderStyle: 'dashed',
              borderWidth: '2px',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer'
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <p className="mb-2">
              <strong>Drag and drop CSV file here</strong>
            </p>
            <p className="text-muted mb-2">or click to browse</p>
            <p className="text-muted small">Maximum file size: 10 MB | Maximum rows: 10,000</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile && (
              <div className="mt-3">
                <span className="badge bg-success">
                  <i className="fas fa-file-csv me-1"></i>
                  {selectedFile.name}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReset();
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Upload Button */}
        {selectedFile && !uploadResults && !processingStatus && (
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={uploading || !healthSystemId}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Starting Upload...
                </>
              ) : (
                <>
                  <i className="fas fa-upload me-2"></i>
                  Upload & Process CSV
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Processing Status */}
        {processingStatus && (
          <div className="processing-status mb-3">
            {/* Only show processing details if NOT completed/failed/partial_success */}
            {processingStatus.status !== 'completed' &&
             processingStatus.status !== 'failed' &&
             processingStatus.status !== 'partial_success' && (
              <>
                {/* Phase indicator */}
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    {(processingStatus.status === 'connecting' || processingStatus.status === 'uploading' || processingStatus.status === 'authenticated') && (
                      <>
                        <div className="spinner-border spinner-border-sm text-secondary me-2"></div>
                        <span>Connected! Uploading and crunching data...</span>
                      </>
                    )}
                    {processingStatus.status === 'validating' && (
                      <>
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>Validating file...</span>
                      </>
                    )}
                    {processingStatus.status === 'harmonizing' && (
                      <>
                        <div className="spinner-grow spinner-grow-sm text-info me-2"></div>
                        <span>AI Intake Agent harmonizing data...</span>
                      </>
                    )}
                    {processingStatus.status === 'processing' && (
                      <>
                        <div className="spinner-border spinner-border-sm text-success me-2"></div>
                        <span>Creating referrals...</span>
                      </>
                    )}
                    {processingStatus.status === 'failed' && (
                      <>
                        <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                        <span>Processing failed</span>
                      </>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="progress" style={{ height: '24px' }}>
                    <div
                      className={`progress-bar ${
                        processingStatus.status === 'harmonizing' ? 'progress-bar-striped progress-bar-animated' :
                        processingStatus.status === 'processing' ? 'progress-bar-striped progress-bar-animated' :
                        ''
                      } ${
                        processingStatus.status === 'failed' ? 'bg-danger' : ''
                      }`}
                      style={{ width: `${processingStatus.progress || 0}%` }}
                    >
                      {processingStatus.progress}%
                    </div>
                  </div>
                </div>

                {/* Detailed message */}
                <div className="alert alert-info small mb-2">
                  {processingStatus.message}
                </div>

                {/* Fallback message (if n8n failed) */}
                {processingStatus.fallback_message && (
                  <div className="alert alert-warning small mb-2">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {processingStatus.fallback_message}
                  </div>
                )}

                {/* DEBUG: Show raw state */}
                {process.env.NODE_ENV === 'development' && (
                  <details className="mb-2">
                    <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                      Debug: View raw state
                    </summary>
                    <pre className="small bg-light p-2" style={{ fontSize: '10px', maxHeight: '200px', overflow: 'auto' }}>
                      {JSON.stringify(processingStatus, null, 2)}
                    </pre>
                  </details>
                )}

                {/* Row progress */}
                {processingStatus.current_row && processingStatus.total_rows && (
                  <div className="text-muted small mb-2">
                    Processing row {processingStatus.current_row} of {processingStatus.total_rows}
                  </div>
                )}

                {/* Error display */}
                {processingStatus.status === 'failed' && processingStatus.error && (
                  <div className="alert alert-danger">
                    <h6 className="alert-heading">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Processing Failed
                    </h6>
                    <p className="mb-2">{processingStatus.error}</p>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleReset}
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Upload Results */}
        {uploadResults && (
          <div className="upload-results">
            <h5 className="mb-3">
              <i className="fas fa-check-circle text-success me-2"></i>
              Processing Complete
            </h5>

            {/* Fallback warning (if n8n harmonization failed AND local processing also failed) */}
            {uploadResults.fallback_message && uploadResults.failed_count > 0 && (
              <div className="alert alert-warning mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {uploadResults.fallback_message}
              </div>
            )}

            {/* Stats */}
            <div className="row mb-3">
              <div className="col-md-4">
                <div className="text-center p-3 border rounded">
                  <div className="h2 text-primary mb-1">{uploadResults.total_rows}</div>
                  <div className="text-muted small">Total Rows</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center p-3 border rounded">
                  <div className="h2 text-success mb-1">{uploadResults.created_count || 0}</div>
                  <div className="text-muted small">Created</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="text-center p-3 border rounded">
                  <div className="h2 text-danger mb-1">{uploadResults.failed_count || 0}</div>
                  <div className="text-muted small">Failed</div>
                </div>
              </div>
            </div>

            {uploadResults.harmonization_used && (
              <div className="mb-3 text-center">
                <span className="badge bg-info">
                  <i className="fas fa-robot me-1"></i>
                  AI Harmonization Applied
                </span>
              </div>
            )}

            {/* Successfully Created */}
            {uploadResults.created && uploadResults.created.length > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-success mb-0">
                    <i className="fas fa-check me-2"></i>
                    Successfully Created ({uploadResults.created.length})
                    {uploadResults.created.some(item => item.warnings && item.warnings.length > 0) && (
                      <span className="badge bg-warning text-dark ms-2">
                        Some with warnings
                      </span>
                    )}
                  </h6>
                  {uploadResults.created.some(item => item.warnings && item.warnings.some(w => w.includes('not found'))) && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-info"
                      onClick={handleToggleAvailableTypes}
                      disabled={loadingAvailableTypes}
                    >
                      {loadingAvailableTypes ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className={`fas fa-${showAvailableTypes ? 'eye-slash' : 'eye'} me-2`}></i>
                          {showAvailableTypes ? 'Hide' : 'View'} Available Task Types
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Available Types Collapsible */}
                {showAvailableTypes && availableTypes && (
                  <div className="alert alert-info mb-3">
                    <h6 className="mb-2">
                      <i className="fas fa-list me-2"></i>
                      Available Task Types for this Health System:
                    </h6>
                    <div className="small" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {availableTypes.length > 0 ? (
                        <ul className="mb-0">
                          {availableTypes.map((type, idx) => (
                            <li key={idx}>{type.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0">No task types found for this health system.</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Row</th>
                        <th>Patient Name</th>
                        <th>Tasks Created</th>
                        <th>Referral ID</th>
                        <th>Warnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResults.created.slice(0, 50).map((item, idx) => (
                        <tr key={idx} className={item.warnings && item.warnings.length > 0 ? 'table-warning' : ''}>
                          <td>{item.row}</td>
                          <td>{item.patient_name}</td>
                          <td>{item.tasks_created}</td>
                          <td className="small text-muted">{item.referral_id?.slice(0, 8)}...</td>
                          <td>
                            {item.warnings && item.warnings.length > 0 ? (
                              <div className="small text-dark">
                                <i className="fas fa-exclamation-triangle text-warning me-1"></i>
                                {item.warnings.join('; ')}
                              </div>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {uploadResults.created.length > 50 && (
                    <div className="small text-muted text-center">
                      ... and {uploadResults.created.length - 50} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Failed Rows */}
            {uploadResults.failed && uploadResults.failed.length > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-danger mb-0">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Failed Rows ({uploadResults.failed.length})
                  </h6>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-info"
                      onClick={handleToggleAvailableTypes}
                      disabled={loadingAvailableTypes}
                    >
                      {loadingAvailableTypes ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className={`fas fa-${showAvailableTypes ? 'eye-slash' : 'eye'} me-2`}></i>
                          {showAvailableTypes ? 'Hide' : 'View'} Available Task Types
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={handleDownloadErrorReport}
                    >
                      <i className="fas fa-download me-2"></i>
                      Download Error Report
                    </button>
                  </div>
                </div>

                {/* Available Types Collapsible */}
                {showAvailableTypes && availableTypes && (
                  <div className="alert alert-info mb-3">
                    <h6 className="mb-2">
                      <i className="fas fa-list me-2"></i>
                      Available Task Types for this Health System:
                    </h6>
                    <div className="small" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {availableTypes.length > 0 ? (
                        <ul className="mb-0">
                          {availableTypes.map((type, idx) => (
                            <li key={idx}>{type.name}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-muted mb-0">No task types found for this health system.</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Row</th>
                        <th>Error</th>
                        <th>Patient Info</th>
                        <th>Conflicting Referral</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResults.failed.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.row}</td>
                          <td className="text-danger small">{item.error}</td>
                          <td className="small">
                            {item.data?.patient_first_name} {item.data?.patient_last_name}
                            {item.data?.patient_dob && ` (${item.data.patient_dob})`}
                          </td>
                          <td className="text-center">
                            {item.existing_referral_id ? (
                              <a
                                href={`/admin/referrals/${item.existing_referral_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                                title="Open conflicting referral in new window"
                              >
                                <i className="fas fa-external-link-alt me-1"></i>
                                View
                              </a>
                            ) : (
                              <span className="text-muted small">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-center gap-3 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReset}
              >
                <i className="fas fa-upload me-2"></i>
                Upload Another File
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (onClose) {
                    onClose();
                  }
                  if (onSuccess) {
                    onSuccess(uploadResults);
                  }
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BulkReferralUpload;
